// Tokens for the Figma section "01 · Entry point" (4488:19598) - page "PAVEL DRAFT". The section
// storyboards the achievements flow on the 390-wide mobile frame: "Modal / Achievements"
// (4488:19617) with its grid and its "Selected for Profile" shelf, and "Modal / Achievement
// Details" (4488:19797) behind a card. Every number in this folder is in that design space and is
// scaled once by the screen, the way the inventory and event folders already do it.
//
// The named colours are the file's own variables, read with get_variable_defs off the modal
// (gold/50, text/primary, text/secondary, text/muted, text/accent, surface/raised,
// surface/sunken, radius/md, radius/full); the gradients and glows are the literal values the
// design context returns, because get_variable_defs hands gradients back unresolved.
import { EVENT_FONT } from '../Event/eventTheme';

export const ACHIEVEMENTS_FONT = EVENT_FONT;

export const DESIGN_FRAME_WIDTH = 390;
export const DESIGN_FRAME_HEIGHT = 844;
/** Wider viewports centre the panel rather than stretching it, same cap as the rest. */
export const MAX_SCALE = 1.15;

/** "Modal / Achievements" sits at 16/44 in the 390 frame; the details modal is 47 shorter. */
export const PANEL_X = 16;
export const PANEL_Y = 44;
export const PANEL_WIDTH = 358;
export const PANEL_HEIGHT = 730;
export const DETAILS_PANEL_HEIGHT = 683;

export const achievementsColors = {
	/** text/primary - an active filter's label. */
	primary: '#FFFFFF',
	/** text/secondary - every resting label. */
	secondary: '#D9C4BC',
	/** text/accent - the "n / 5" counter and the icon strokes. */
	accent: '#FFFF99',
	/** text/muted - a locked card's caption and the empty shelf's line. */
	muted: '#A88E86',
	/** gold/50 - the modal titles, card captions and progress values. */
	gold50: '#FFF5B9',
	/** gold/100 - the detail title and the earned tier's name. */
	gold100: '#FFE9A8',
	/** gold/200 - the profile modal's stat values and coin balance. */
	gold200: '#FFD766',
	/** gold/150, gold/500, gold/700 - the progress fill's ramp; gold/500 is also the earned rail. */
	gold150: '#FFEE82',
	gold500: '#F5A300',
	gold700: '#CE7C00',
	/** surface/raised - the shelf and the progress panel. */
	surface: '#330E07',
	/** surface/sunken - the header's round back button. */
	sunken: '#0F0301',
	/** surface/overlay - the remove control's fill and the progress track. */
	overlay: '#4A1A0E',
	/** The panel's own outline, and an unlocked card's. */
	panelBorder: '#FCF17B',
	/** border/red - a resting filter and an empty profile slot. */
	border: '#6B1A10',
	/** The active filter's brighter outline. */
	borderActive: '#FF6A4D',
	/** The shelf's and the progress panel's duller orange hairline. */
	panelOrange: '#DA600E',
	/** A locked card: its plate, its hairline and the remaining half of the tier rail. */
	lockedPlate: '#241B19',
	lockedBorder: '#4A3B38',
	/** The padlock indicator's olive-gold outline. */
	lockedIndicatorBorder: '#A39357',
	/** progress-bar-level-green - the check on a card that is on the profile. */
	green: '#4ABF54',
	/** An empty profile slot's plate. */
	emptySlot: '#2A0A05',
	/** "First TXT" (4488:19964) - the plate over an empty shelf. */
	emptyShelfPlate: 'rgba(0,0,0,0.3)',
	/** Button / Remove from Profile's plate, and the app's well elsewhere. */
	well: '#1A0603',
	greenStroke: '#B8FF8C',
	/** "Scrim" (4488:19603) - the same warm near-black the other modals lay over the app. */
	scrim: 'rgba(11,2,1,0.45)',
	dialogScrim: 'rgba(11,2,1,0.72)',
	dialogTop: '#330200',
	dialogBottom: '#120000',
	goldStroke: '#FFE45C',
	onGold: '#2A0A04',
} as const;

export const RADIUS_MD = 8;
export const RADIUS_CARD = 10;
export const RADIUS_PANEL = 16;
export const RADIUS_FULL = 999;

/** An unlocked card, and the mini card on the profile shelf. */
export const CARD_FILL: Array<[string, string]> = [
	['0', '#330B05'],
	['1', '#0F0402'],
];
/** Filter pill at rest. */
export const FILTER_FILL: Array<[string, string]> = [
	['0', '#330200'],
	['1', '#120000'],
];
/** Filter pill when it is the active one. */
export const FILTER_ACTIVE_FILL: Array<[string, string]> = [
	['0', '#E0230F'],
	['1', '#8C0D05'],
];
/** "Button / Add to Profile" (4488:19844). */
export const GREEN_BUTTON_FILL: Array<[string, string]> = [
	['0', '#66D93D'],
	['0.55', '#248F1C'],
	['1', '#0D540F'],
];
export const GOLD_BUTTON_FILL: Array<[string, string]> = [
	['0', '#FFF5B9'],
	['0.35', '#FFEE82'],
	['0.78', '#F5A300'],
	['1', '#CE7C00'],
];
/** The detail screen's progress fill. */
export const PROGRESS_FILL: Array<[string, string]> = [
	['0', '#FFF5B9'],
	['0.35', '#FFEE82'],
	['0.75', '#F5A300'],
	['1', '#CE7C00'],
];

const glow = (color: string, opacity: number, radius: number) =>
	({
		shadowColor: color,
		shadowOffset: { width: 0, height: 0 },
		shadowOpacity: opacity,
		shadowRadius: radius,
		elevation: Math.round(radius * 0.5),
	}) as const;

/**
 * React Native takes one shadow per view, so where the file paints a pair these keep the wider,
 * warmer half - the one that carries the look. The panel's own pair is an outer
 * `0 0 10 rgba(255,114,0,0.7)` and an inset red; only the outer one is reproducible here.
 */
export const achievementsGlow = {
	panel: glow('#FF7200', 0.7, 10),
	/** "Dropdown / Categories" (4395:3713) lifts off the grid with `0 10 12 rgba(0,0,0,0.6)`. */
	menu: {
		shadowColor: '#000000',
		shadowOffset: { width: 0, height: 10 },
		shadowOpacity: 0.6,
		shadowRadius: 12,
		elevation: 12,
	} as const,
	filterActive: glow('#FF7200', 0.45, 16),
	hero: glow('#FF9C00', 0.55, 24),
	green: glow('#40FF33', 0.45, 4.5),
	gold: glow('#FF9E14', 0.5, 5),
	dialog: glow('#FF7200', 0.45, 24),
} as const;
